// Pagali Food › Restaurantes — contactos de cada restaurante:
//  - WhatsApp: botão flutuante na página do restaurante (app e site);
//  - Telefone de pedidos: recebe o SMS de cada pedido pago;
//  - Avaliação: média da comida e as avaliações dos clientes;
//  - Canais: aparece no Pagali Food (app e site), no Chat-to-Pay (WhatsApp) ou nos dois.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  Tooltip, Snackbar, CircularProgress, Link, Rating, Switch, FormControlLabel,
} from '@mui/material';
import { Edit, Refresh, WhatsApp, Sms, Place, AddPhotoAlternate, DeleteOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AvaliacoesDialog from './AvaliacoesDialog';

interface Restaurante {
  id: number;
  name: string;
  entityName?: string;
  address?: string;
  status: string;
  montraVisivel?: number | boolean;
  whatsapp?: string | null;
  orderPhone?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  ratingMedia?: number | string | null;
  ratingCount?: number;
  imageUrl?: string | null;   // capa própria (Admin)
  coverImage?: string | null; // o que a montra mostra: a capa ou a foto de um prato
}

/** 2389912345 → +238 991 23 45 */
const mostrarNumero = (n?: string | null) => {
  if (!n) return '';
  const m = n.match(/^238(\d{3})(\d{2})(\d{2})$/);
  return m ? `+238 ${m[1]} ${m[2]} ${m[3]}` : `+${n}`;
};

/** Estado do restaurante no Chat-to-Pay (loja «food:<ID>»): sem registo = por configurar */
interface CanalChat { no_canal: boolean; activo: boolean }
/** Mudança de canal à espera de confirmação */
interface MudarCanal { r: Restaurante; canal: 'FOOD' | 'CHAT'; ligar: boolean }

const FoodRestaurantes: React.FC = () => {
  const navigate = useNavigate();
  const [lista, setLista] = useState<Restaurante[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Restaurante | null>(null);
  const [form, setForm] = useState({ whatsapp: '', orderPhone: '', localizacao: '' });
  const [saving, setSaving] = useState(false);
  const [erroForm, setErroForm] = useState<string | null>(null);
  const [ok, setOk] = useState('');
  const [avaliacoes, setAvaliacoes] = useState<Restaurante | null>(null);
  const [capaBusy, setCapaBusy] = useState(false);
  // Interruptor do Pagali Food para os clientes (pg_config FOOD_APP_VISIVEL)
  const [visivel, setVisivel] = useState<boolean | null>(null);
  const [confirmarVisivel, setConfirmarVisivel] = useState<boolean | null>(null);
  const [chat, setChat] = useState<Record<string, CanalChat>>({});
  const [mudarCanal, setMudarCanal] = useState<MudarCanal | null>(null);
  const [canalBusy, setCanalBusy] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, coms] = await Promise.all([
        api.get('/food/restaurants', { params: { limit: 100 } }),
        api.get('/admin/chat-to-pay/comerciantes').catch(() => null),
      ]);
      setLista(res.data.data || []);
      const porLoja: Record<string, CanalChat> = {};
      for (const c of coms?.data?.data || []) if (c.food) porLoja[c.loja_id] = { no_canal: !!c.no_canal, activo: !!c.activo };
      setChat(porLoja);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível carregar os restaurantes');
    }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    api.get('/admin/config')
      .then((res) => setVisivel(res.data.data?.FOOD_APP_VISIVEL?.value === 'true'))
      .catch(() => setVisivel(null));
  }, []);

  const mudarVisivel = async (v: boolean) => {
    setConfirmarVisivel(null);
    try {
      await api.put('/admin/config', { FOOD_APP_VISIVEL: v ? 'true' : 'false' });
      setVisivel(v);
      setOk(v ? 'Pagali Food aberto aos clientes' : 'Pagali Food escondido dos clientes');
    } catch (e: any) {
      setError(e?.response?.data?.errors?.join(' · ') || e?.response?.data?.error || 'Não foi possível mudar o interruptor');
    }
  };

  const aplicarCanal = async () => {
    if (!mudarCanal) return;
    const { r, canal, ligar } = mudarCanal;
    setCanalBusy(true);
    try {
      if (canal === 'FOOD') await api.put(`/food/restaurants/${r.id}`, { montraVisivel: ligar });
      else await api.put(`/admin/chat-to-pay/comerciantes/${encodeURIComponent(`food:${r.id}`)}`, { activo: ligar });
      const nome = canal === 'FOOD' ? 'Pagali Food' : 'Chat-to-Pay';
      setOk(`${r.name} ${ligar ? 'aparece agora' : 'deixou de aparecer'} no ${nome}`);
      setMudarCanal(null);
      carregar();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível mudar o canal');
      setMudarCanal(null);
    }
    setCanalBusy(false);
  };

  const abrir = (r: Restaurante) => {
    setEditing(r);
    setErroForm(null);
    setForm({
      whatsapp: mostrarNumero(r.whatsapp),
      orderPhone: mostrarNumero(r.orderPhone),
      localizacao: r.latitude != null && r.longitude != null ? `${Number(r.latitude)}, ${Number(r.longitude)}` : '',
    });
  };

  // Capa do restaurante: carregar (fica logo gravada) ou tirar (volta a foto de um prato)
  const carregarCapa = async (f?: File) => {
    if (!editing || !f) return;
    setCapaBusy(true);
    setErroForm(null);
    try {
      const fd = new FormData();
      fd.append('capa', f);
      const r = await api.post(`/food/restaurants/${editing.id}/capa`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.data?.imageUrl;
      setEditing((e) => (e ? { ...e, imageUrl: url, coverImage: url } : e));
      setOk('Capa gravada');
      carregar();
    } catch (e: any) {
      setErroForm(e?.response?.data?.error || 'Não foi possível gravar a capa');
    }
    setCapaBusy(false);
  };
  const tirarCapa = async () => {
    if (!editing) return;
    setCapaBusy(true);
    try {
      await api.delete(`/food/restaurants/${editing.id}/capa`);
      setEditing((e) => (e ? { ...e, imageUrl: null } : e));
      setOk('Capa retirada — volta a foto de um prato');
      carregar();
    } catch (e: any) {
      setErroForm(e?.response?.data?.error || 'Não foi possível tirar a capa');
    }
    setCapaBusy(false);
  };

  const gravar = async () => {
    if (!editing) return;
    setSaving(true);
    setErroForm(null);
    try {
      // «14.9213, -23.5280» — copiado do Google Maps (clique direito no sítio → as coordenadas)
      const loc = form.localizacao.trim();
      let latitude: number | null = null;
      let longitude: number | null = null;
      if (loc) {
        const m = loc.match(/^\s*(-?\d+(?:\.\d+)?)\s*[,; ]\s*(-?\d+(?:\.\d+)?)\s*$/);
        if (!m || Math.abs(Number(m[1])) > 90 || Math.abs(Number(m[2])) > 180) {
          setErroForm('Localização inválida: use «latitude, longitude», por exemplo 14.9213, -23.5280');
          setSaving(false);
          return;
        }
        latitude = Number(m[1]);
        longitude = Number(m[2]);
      }
      await api.put(`/food/restaurants/${editing.id}`, { whatsapp: form.whatsapp, orderPhone: form.orderPhone, latitude, longitude });
      setOk(`${editing.name} actualizado`);
      setEditing(null);
      carregar();
    } catch (e: any) {
      setErroForm(e?.response?.data?.error || 'Não foi possível gravar');
    }
    setSaving(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Restaurantes</Typography>
          <Typography variant="body2" color="text.secondary">
            O WhatsApp aparece como botão flutuante na página do restaurante (app e site). O telefone de pedidos recebe um SMS por cada pedido pago.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {visivel !== null && (
        <Alert severity={visivel ? 'success' : 'warning'} sx={{ mb: 2 }}
          action={
            <FormControlLabel
              control={<Switch checked={visivel} onChange={(e) => setConfirmarVisivel(e.target.checked)} />}
              label={visivel ? 'Aberto' : 'Escondido'}
            />
          }>
          {visivel
            ? 'O Pagali Food está visível aos clientes na app (montra e carrossel no início).'
            : 'O Pagali Food está escondido dos clientes: a montra vem vazia e a app não mostra a secção. Só os administradores a vêem, para testar.'}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Capa</TableCell>
              <TableCell>Restaurante</TableCell>
              <TableCell>Entidade</TableCell>
              <TableCell>Canais</TableCell>
              <TableCell>WhatsApp</TableCell>
              <TableCell>Telefone de pedidos (SMS)</TableCell>
              <TableCell>Localização (recolha)</TableCell>
              <TableCell>Avaliação</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : lista.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>Sem restaurantes</TableCell></TableRow>
            ) : lista.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Tooltip title={r.imageUrl ? 'Capa própria' : 'Sem capa própria: a montra usa a foto de um prato'}>
                    <Box sx={{ width: 72, height: 48, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover', position: 'relative' }}>
                      {(r.imageUrl || r.coverImage) && (
                        <img src={(r.imageUrl || r.coverImage) as string} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Typography variant="caption" color={r.imageUrl ? 'success.main' : 'text.disabled'}>
                    {r.imageUrl ? 'Própria' : 'Foto de um prato'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{r.name}</Typography>
                  {r.address && <Typography variant="caption" color="text.secondary">{r.address}</Typography>}
                </TableCell>
                <TableCell>{r.entityName || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Montra do Pagali Food na app e no site">
                    <FormControlLabel sx={{ display: 'flex', m: 0 }}
                      control={<Switch size="small" checked={Number(r.montraVisivel ?? 1) === 1}
                        onChange={(e) => setMudarCanal({ r, canal: 'FOOD', ligar: e.target.checked })} />}
                      label={<Typography variant="body2">Pagali Food</Typography>} />
                  </Tooltip>
                  {/* Sem restaurantes no Chat-to-Pay (servidor sem a integração Food) não há nada a mostrar */}
                  {Object.keys(chat).length === 0 ? null : chat[`food:${r.id}`]?.no_canal ? (
                    <Tooltip title="Pedidos pelo WhatsApp do restaurante">
                      <FormControlLabel sx={{ display: 'flex', m: 0 }}
                        control={<Switch size="small" checked={chat[`food:${r.id}`].activo}
                          onChange={(e) => setMudarCanal({ r, canal: 'CHAT', ligar: e.target.checked })} />}
                        label={<Typography variant="body2">Chat-to-Pay</Typography>} />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Falta o número de WhatsApp e quem recebe — configure em Chat-to-Pay">
                      <Link component="button" variant="body2" underline="hover" sx={{ display: 'block', ml: 1, mt: 0.5 }}
                        onClick={() => navigate('/chat-to-pay')}>
                        Chat-to-Pay: configurar
                      </Link>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {r.whatsapp ? (
                    <Link href={`https://wa.me/${r.whatsapp}`} target="_blank" rel="noopener" underline="hover"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <WhatsApp fontSize="small" sx={{ color: '#25D366' }} /> {mostrarNumero(r.whatsapp)}
                    </Link>
                  ) : <Typography variant="body2" color="text.disabled">Sem número — o botão não aparece</Typography>}
                </TableCell>
                <TableCell>
                  {r.orderPhone ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Sms fontSize="small" color="action" /> {mostrarNumero(r.orderPhone)}
                    </Box>
                  ) : <Typography variant="body2" color="text.disabled">Sem número — pedidos sem SMS</Typography>}
                </TableCell>
                <TableCell>
                  {r.latitude != null && r.longitude != null ? (
                    <Link href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`} target="_blank" rel="noopener"
                      underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Place fontSize="small" color="primary" /> Ver no mapa
                    </Link>
                  ) : <Typography variant="body2" color="text.disabled">Sem ponto — o estafeta só vê a morada</Typography>}
                </TableCell>
                <TableCell>
                  {r.ratingCount ? (
                    <Tooltip title={r.ratingCount < 5 ? 'Na app e no site a nota só aparece a partir de 5 avaliações' : 'Ver avaliações'}>
                      <Link component="button" underline="hover" onClick={() => setAvaliacoes(r)}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating value={Number(r.ratingMedia)} precision={0.1} readOnly size="small" />
                        {Number(r.ratingMedia).toFixed(1)} ({r.ratingCount})
                      </Link>
                    </Tooltip>
                  ) : <Typography variant="body2" color="text.disabled">Sem avaliações</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar capa, contactos e localização">
                    <IconButton onClick={() => abrir(r)}><Edit /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Capa, contactos e localização · {editing?.name}</DialogTitle>
        <DialogContent>
          {erroForm && <Alert severity="error" sx={{ mb: 2 }}>{erroForm}</Alert>}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Capa do restaurante</Typography>
          <Box sx={{ mt: 1, mb: 1, height: 140, borderRadius: 2, overflow: 'hidden', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {editing && (editing.imageUrl || editing.coverImage)
              ? <img src={(editing.imageUrl || editing.coverImage) as string} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <Typography variant="body2" color="text.disabled">Sem imagem</Typography>}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {editing?.imageUrl
              ? 'Capa própria — aparece na montra, no carrossel do início e no topo da página do restaurante (app e site).'
              : 'Sem capa própria: a montra usa a foto de um prato principal. Carregue uma imagem horizontal (ex.: 1200×600), JPEG/PNG/WebP até 5 MB.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button component="label" variant="outlined" size="small" startIcon={capaBusy ? <CircularProgress size={16} /> : <AddPhotoAlternate />} disabled={capaBusy}>
              {editing?.imageUrl ? 'Trocar capa' : 'Carregar capa'}
              <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { carregarCapa(e.target.files?.[0]); e.target.value = ''; }} />
            </Button>
            {editing?.imageUrl && (
              <Button variant="text" color="error" size="small" startIcon={<DeleteOutline />} onClick={tirarCapa} disabled={capaBusy}>
                Tirar capa
              </Button>
            )}
          </Box>
          <TextField
            fullWidth margin="normal" label="WhatsApp do restaurante" placeholder="+238 991 23 45"
            value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
            helperText="Vazio = sem botão de WhatsApp na página do restaurante"
            InputProps={{ startAdornment: <WhatsApp sx={{ color: '#25D366', mr: 1 }} /> }}
          />
          <TextField
            fullWidth margin="normal" label="Telefone de pedidos (SMS)" placeholder="+238 991 23 45"
            value={form.orderPhone} onChange={(e) => setForm((f) => ({ ...f, orderPhone: e.target.value }))}
            helperText="Recebe um SMS por cada pedido pago"
            InputProps={{ startAdornment: <Sms color="action" sx={{ mr: 1 }} /> }}
          />
          <TextField
            fullWidth margin="normal" label="Localização do restaurante" placeholder="14.9213, -23.5280"
            value={form.localizacao} onChange={(e) => setForm((f) => ({ ...f, localizacao: e.target.value }))}
            helperText="Latitude, longitude — no Google Maps, clique com o botão direito no restaurante e copie as coordenadas. É onde o estafeta vai recolher."
            InputProps={{ startAdornment: <Place color="primary" sx={{ mr: 1 }} /> }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancelar</Button>
          <Button variant="contained" onClick={gravar} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmarVisivel !== null} onClose={() => setConfirmarVisivel(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmarVisivel ? 'Abrir o Pagali Food aos clientes?' : 'Esconder o Pagali Food dos clientes?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmarVisivel
              ? 'Os restaurantes activos passam a aparecer na app a todos os clientes (pode levar até 1 minuto). Confirme que as ementas, os termos e os estafetas estão prontos.'
              : 'A montra deixa de mostrar restaurantes aos clientes. Os pedidos em curso continuam.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarVisivel(null)}>Cancelar</Button>
          <Button variant="contained" color={confirmarVisivel ? 'primary' : 'warning'}
            onClick={() => mudarVisivel(!!confirmarVisivel)}>
            {confirmarVisivel ? 'Abrir' : 'Esconder'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!mudarCanal} onClose={() => setMudarCanal(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {mudarCanal && `${mudarCanal.ligar ? 'Mostrar' : 'Esconder'} ${mudarCanal.r.name} ${mudarCanal.ligar ? 'no' : 'do'} ${mudarCanal.canal === 'FOOD' ? 'Pagali Food' : 'Chat-to-Pay'}?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {mudarCanal?.canal === 'FOOD'
              ? (mudarCanal.ligar
                ? 'O restaurante volta a aparecer na montra da app e do site, e os clientes podem encomendar por lá.'
                : 'O restaurante sai da montra da app e do site e deixa de aceitar pedidos por lá. Os pedidos em curso continuam; o Chat-to-Pay não muda.')
              : (mudarCanal?.ligar
                ? 'O WhatsApp do restaurante passa a mostrar a ementa e a aceitar pedidos.'
                : 'O WhatsApp do restaurante deixa de aceitar pedidos. A montra do Pagali Food não muda.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMudarCanal(null)}>Cancelar</Button>
          <Button variant="contained" color={mudarCanal?.ligar ? 'primary' : 'warning'} onClick={aplicarCanal} disabled={canalBusy}>
            {canalBusy ? <CircularProgress size={20} /> : mudarCanal?.ligar ? 'Mostrar' : 'Esconder'}
          </Button>
        </DialogActions>
      </Dialog>

      <AvaliacoesDialog open={!!avaliacoes} onClose={() => setAvaliacoes(null)}
        titulo={avaliacoes?.name || ''} filtro={{ restaurantId: avaliacoes?.id }} />

      <Snackbar open={!!ok} autoHideDuration={3000} onClose={() => setOk('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success">{ok}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FoodRestaurantes;
